// @ts-check
import Vue from 'vue';
import { BETSLIP_MSG_STATUS } from 'bto-models/betslip/const';
import { openModal } from '@bto-services/modal';
import { minutesDiff } from '@bto-services/utils';
import { toastStore, saveToastStore } from './toast.store';
import { saveUserBetslipsStore, userBetslipsStore } from './user-betslips.store';

export { toastStore } from './toast.store';
export { userBetslipsStore } from './user-betslips.store';

/**
 * @typedef {import('bto-models/toast-notification/interfaces').ToastNotification} ToastNotification
 */

/**
 * Set timeout and return it
 * @param {string} key - toast uniqure identifier
 * @param {number} remainingTime - timeout time
 */
function createTimeout(key, remainingTime) {
  return setTimeout(() => {
    const msg = toastStore.messages[key];
    if (msg && msg.betslip && msg.betslip.status === BETSLIP_MSG_STATUS.PENDING) {
      openModal({
        title: 'BS.NotificationExpired',
        modalClass: 'bto-sb-modalpopup-md',
        message: 'BS.NotificationExpiredInfo',
      });
    }
    removeToastNotification(key);
  }, remainingTime);
}

/**
 * Set toast notification
 * @param {string} key - toast unique identifier
 * @param {ToastNotification} payload - toast notification 
 */
export const setToastNotification = (key, payload) => {
  if (toastStore.messages[key]) {
    clearTimeout(toastStore.messages[key].timeout);
  }

  const remainingTime = payload.expireTime - Date.now();
  const timeout = createTimeout(key, remainingTime);

  Vue.set(toastStore.messages, key, { ...payload, timeout });
  saveToastStore();
};

/**
 * Add user betslip into user betslip store for rebet
 * @param {string} key - user betslip unique identifier [userId_identifier]
 * @param {import('bto-models/betslip').BetslipStore} betslipStore - betslip store
 */
export const addUserBetslip = (key, betslipStore) => {
  userBetslipsStore[key] = { ...betslipStore, placeTime: Date.now() };
  saveUserBetslipsStore();
};

/**
 * Remove toast notification
 * @param {string} key - toast unique identifier
 * @param {boolean} [skipSave] - flag to skip saving state to local storage 
 */
export const removeToastNotification = (key, skipSave = false) => {
  clearTimeout(toastStore.messages[key].timeout);
  // If we have user betslip and we reloaded the page or keepBetslip flag is false delete user betslip from storage
  if (userBetslipsStore[key] && (skipSave || !toastStore.messages[key].keepBetslip)) {
    delete userBetslipsStore[key];
    saveUserBetslipsStore();
  }
  Vue.delete(toastStore.messages, key);
  if (skipSave) {
    return;
  }
  saveToastStore();
};

/**
 * On app load init toast state,  
 * clean expired messages,  
 * set timer for messages that have yet to expire
 */
export const initToastState = () => {
  const toasts = Object.entries(toastStore.messages);
  const now = Date.now();
  toasts.forEach(([key, v]) => {
    const remainingTime = v.expireTime - now;
    if (remainingTime <= 1000) {
      removeToastNotification(key, true);
      return;
    }
    const timeout = createTimeout(key, remainingTime);

    toastStore.messages[key].timeout = timeout;
  });
  // Check saved betslipStores if there are any that are still in storage after 10 min remove them 
  const userBetslips = Object.entries(userBetslipsStore);
  userBetslips.forEach(([key, v]) => {
    const passedMinutes = minutesDiff(v.placeTime, now);
    if (passedMinutes >= 10) {
      delete userBetslipsStore[key];
    }
  });
  saveUserBetslipsStore();
  saveToastStore();
};